/**
 * Entity workbench API (ENTITY_WORKBENCH_PLAN.md W1 + W2).
 *
 * One endpoint per tab; header data rides the overview fetch. The
 * Identity tab shows Cynapse (the record) beside Assayer local state
 * (the projection), and triage actions carry the operator's audit
 * identity server-side — nothing auto-resolves.
 */
import { apiClient } from './config.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkbenchHeader {
  entity_id: string;
  display_name: string;
  entity_type: string;
  ticker: string | null;
  exchange: string;
  approval_status: string | null;
  discovery_status: string | null;
  cynapse_entity_id: string | null;
  identity_refreshed_at: string | null;
  price: { close: number | null; as_at: string | null; source: string | null } | null;
  index_badges: { index_code: string; exchange: string; state: string }[];
  articles_published: number;
  candidates_awaiting_triage: number | null;
}

export interface WorkbenchOverview {
  header: WorkbenchHeader;
  overview: {
    description: string | null;
    description_updated_at: string | null;
    sector: string | null;
    latest_announcements: {
      announcement_id: string;
      title: string | null;
      date: string | null;
      price_sensitive: boolean | null;
      source: string | null;
    }[];
    latest_articles: {
      article_id: string;
      title: string | null;
      publish_status: string | null;
      published_at: string | null;
      published_slug: string | null;
    }[];
    open_signals: {
      signal_id: string;
      signal_type: string;
      ticker: string | null;
      exchange: string | null;
      created_at: string;
    }[];
    next_calendar_event: {
      event_type: string;
      event_date: string;
      source: string | null;
      // Text label ('forecast' | 'confirmed'), not a number.
      confidence: string | null;
    } | null;
  };
}

export interface PresenceRow {
  presence_id: string;
  attribute_kind: string;
  value_raw: string;
  value_norm: string;
  valid_from?: string | null;
  valid_to?: string | null;
  source: string;
  evidence_ref: string | null;
  // NUMERIC arrives as a string through the Cynapse proxy chain —
  // always coerce with Number() before formatting.
  confidence: number | string;
  observation_count: number;
  observed_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface PresenceCheck {
  attribute_kind: string;
  result: string;
  value_norm: string | null;
  source: string;
  checked_at: string;
  evidence_ref: string | null;
}

export interface WorkbenchIdentity {
  entity_id: string;
  cynapse_entity_id: string | null;
  record: {
    available: boolean;
    canonical_name?: string | null;
    canonical_domain?: string | null;
    last_verification?: string | null;
    current?: PresenceRow[];
    candidates?: PresenceRow[];
    latest_checks?: PresenceCheck[];
    current_codes?: { code_system: string; code_value: string; valid_from: string | null }[];
    historical_codes?: { code_system: string; code_value: string; valid_from: string | null; valid_to: string | null }[];
    active_aliases?: { alias_text: string; alias_kind: string | null }[];
    securities?: { exchange: string; code: string; security_class: string; is_primary_line: boolean; valid_to: string | null }[];
  };
  local: {
    canonical_identifier: string | null;
    display_name: string;
    canonical_domain: string | null;
    legacy_domain_keys: Record<string, string>;
    linkedin_url: string | null;
    linkedin_urn: string | null;
    twitter_handle: string | null;
    identity_refreshed_at: string | null;
  };
}

export type TriageAction = 'accept_correct' | 'accept_rebrand' | 'reject';

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export async function getEntityOverview(entityId: string): Promise<WorkbenchOverview> {
  const { data } = await apiClient.get<WorkbenchOverview>(
    `/admin/entities/${entityId}/overview`
  );
  return data;
}

export async function getEntityIdentity(entityId: string): Promise<WorkbenchIdentity> {
  const { data } = await apiClient.get<WorkbenchIdentity>(
    `/admin/entities/${entityId}/identity`
  );
  return data;
}

export async function triageIdentityCandidate(
  entityId: string,
  body: { presence_id: string; action: TriageAction; effective_date?: string }
): Promise<{ resolved: boolean; action: string }> {
  const { data } = await apiClient.post(
    `/admin/entities/${entityId}/identity/triage`,
    body
  );
  return data;
}

// ---------------------------------------------------------------------------
// Contacts tab (W8)
// ---------------------------------------------------------------------------
// Two populations: organisational channels (entity_contacts — an inbox belongs
// to a company) and people (aggregated from extraction, keyed on lower(email)
// per R16). The person drawer is cross-entity by design, so it is NOT nested
// under an entity id.

export interface ContactChannel {
  contact_id: string;
  channel_email: string | null;
  channel_phone: string | null;
  contact_type: string;
  role_label: string | null;
  contact_name_raw: string | null;
  is_notification_target: boolean;
  confidence: number | string | null;
  source_section: string | null;
  created_via: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  source_announcement_id: string | null;
  source_announcement_title: string | null;
  source_announcement_date: string | null;
  sent_count: number;
  last_sent: string | null;
  last_delivery_status: string | null;
  suppressed: boolean;
}

export interface ContactPerson {
  email: string;
  full_name: string | null;
  job_title: string | null;
  organization: string | null;
  role: string | null;
  contact_type: string | null;
  phone: string | null;
  confidence: number | string | null;
  sightings: number;
  announcements: number;
  first_seen: string | null;
  last_seen: string | null;
  review_id: string | null;
  approval_status: 'pending' | 'approved' | 'rejected' | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  notes: string | null;
  sent_count: number;
  last_sent: string | null;
  last_delivery_status: string | null;
  suppressed: boolean;
  other_entity_count: number;
}

/** One holding at the company: (person × role) folded over every filing
 *  that named it; every label used rides along. `effective_from` is free text as extracted — never
 *  a date. `first_seen`/`last_seen`/`effective_to` are ISO dates (Sydney). */
export interface KeyRoleHolding {
  person_key: string;
  person_name: string;
  person_entity_id: string | null;
  /** The resolved id also carries a different person's name — a resolver
   *  mis-bind; the panel has split them by name and flags both. */
  shared_person_id: boolean;
  role: string;
  role_label: string;
  labels_seen: string[];
  /** Titled seats that closed while the holding continued under another label. */
  closed_seats: { label: string; effective_to: string | null; successor_name: string | null }[];
  is_current: boolean;
  change_type: string | null;
  confidence: string | null;
  effective_from: string | null;
  effective_to: string | null;
  successor_name: string | null;
  filings: number;
  first_seen: string | null;
  last_seen: string | null;
  latest_announcement_id: string | null;
  latest_announcement_title: string | null;
  binding_ids: string[];
}

export interface EntityContacts {
  entity_id: string;
  entity_name: string;
  ticker: string | null;
  key_roles: { current: KeyRoleHolding[]; former: KeyRoleHolding[] };
  channels: ContactChannel[];
  people: ContactPerson[];
}

export interface PersonDetail {
  email: string;
  profile: {
    full_name: string | null;
    job_title: string | null;
    organization: string | null;
    phone: string | null;
    name_variants: string[];
    org_variants: string[];
    confidence: number | string | null;
    sightings: number;
    first_seen: string | null;
    last_seen: string | null;
  };
  entities: {
    entity_id: string;
    display_name: string;
    ticker: string | null;
    entity_type: string;
    role: string | null;
    sightings: number;
    announcements: number;
    first_seen: string | null;
    last_seen: string | null;
    approval_status: 'pending' | 'approved' | 'rejected' | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
  }[];
  appearances: {
    announcement_id: string;
    title: string | null;
    date: string | null;
    entity_id: string | null;
    ticker: string | null;
    entity_name: string | null;
    role: string | null;
    job_title: string | null;
    organization: string | null;
    confidence: number | string | null;
    extracted_at: string | null;
  }[];
  sends: {
    notification_id: string;
    article_id: string | null;
    article_title: string | null;
    entity_id: string | null;
    ticker: string | null;
    entity_name: string | null;
    target: string | null;
    sent_at: string | null;
    status: string;
    reason: string | null;
    delivery_status: string | null;
    delivery_event_at: string | null;
  }[];
  sent_total: number;
  suppression: {
    scope: string | null;
    reason: string | null;
    source: string | null;
    suppressed_at: string | null;
    suppressed_by: string | null;
    notes: string | null;
  } | null;
  // Cynapse is the person record; `likely_duplicates` are ITS merge
  // candidates — shown, never merged here (R16).
  cynapse: {
    available: boolean;
    entity_id?: string;
    full_name?: string | null;
    current_employer?: string | null;
    job_title?: string | null;
    match_method?: string | null;
    email_addresses?: { type: string | null; email: string; verified?: boolean | null }[];
    likely_duplicates?: {
      entity_id: string;
      canonical_name: string | null;
      current_employer: string | null;
      confidence: number;
      reason: string;
    }[];
  };
}

export type PersonApprovalAction = 'approve' | 'reject' | 'reset';

export async function getEntityContacts(entityId: string): Promise<EntityContacts> {
  const { data } = await apiClient.get<EntityContacts>(`/admin/entities/${entityId}/contacts`);
  return data;
}

export async function addEntityChannel(
  entityId: string,
  body: {
    channel_email?: string | null;
    channel_phone?: string | null;
    contact_type: string;
    contact_name_raw?: string | null;
    role_label?: string | null;
    is_notification_target?: boolean;
  }
): Promise<{ contact_id: string }> {
  const { data } = await apiClient.post(`/admin/entities/${entityId}/contacts/channels`, body);
  return data;
}

export async function updateEntityChannel(
  entityId: string,
  contactId: string,
  body: Partial<{
    channel_email: string | null;
    channel_phone: string | null;
    contact_type: string;
    contact_name_raw: string | null;
    role_label: string | null;
    is_notification_target: boolean;
  }>
): Promise<{ contact_id: string; is_notification_target: boolean }> {
  const { data } = await apiClient.patch(
    `/admin/entities/${entityId}/contacts/channels/${contactId}`,
    body
  );
  return data;
}

export async function deleteEntityChannel(
  entityId: string,
  contactId: string
): Promise<{ deleted: boolean }> {
  const { data } = await apiClient.delete(
    `/admin/entities/${entityId}/contacts/channels/${contactId}`
  );
  return data;
}

export async function setPersonApproval(
  entityId: string,
  body: { email: string; action: PersonApprovalAction; notes?: string }
): Promise<{ review_id: string; status: string }> {
  const { data } = await apiClient.post(
    `/admin/entities/${entityId}/contacts/people/approval`,
    body
  );
  return data;
}

export async function getPersonDetail(email: string): Promise<PersonDetail> {
  const { data } = await apiClient.get<PersonDetail>('/admin/entities/contacts/person', {
    params: { email },
  });
  return data;
}

export async function updatePerson(body: {
  email: string;
  full_name: string;
  job_title?: string | null;
  organisation?: string | null;
  phone?: string | null;
  role?: string;
  cynapse_entity_id?: string | null;
}): Promise<Record<string, unknown>> {
  const { data } = await apiClient.patch('/admin/entities/contacts/person', body);
  return data;
}

// ---------------------------------------------------------------------------
// Backfill history (Manage tab) — HISTORICAL_BACKFILL_PUBLISH_PLAN.md
// ---------------------------------------------------------------------------
// After-the-fact articles for a coverage customer's / prospect's hub: dated to
// the filing's session day, silent (no socials, no notification draft),
// excluded from the day's counts. GET is the dry run; POST applies. The
// backend refuses (409) an entity outside the coverage groups.

export type BackfillOutcome = 'covered' | 'publish_draft' | 'generate' | 'blocked';

export interface BackfillProductOutcome {
  product_id: string;
  product_name: string;
  target_name: string | null;
  outcome: BackfillOutcome;
  article_id: string | null;
  article_status: string | null;
}

export interface BackfillCandidate {
  announcement_id: string;
  title: string | null;
  announcement_date: string | null;
  session_date: string | null;
  source_date: string | null;
  substance: number | null;
  content_types: string[];
  status: 'eligible' | 'excluded';
  reason: string | null;
  products: BackfillProductOutcome[];
}

export interface BackfillPlan {
  entity_id: string;
  groups: string[];
  months: number;
  limit: number;
  dry_run: boolean;
  summary: Record<string, number>;
  candidates: BackfillCandidate[];
  enqueued?: {
    announcement_id: string;
    product_id: string;
    product_name: string;
    outcome: BackfillOutcome;
    job_id?: string;
    article_id?: string;
    error?: string;
  }[];
}

export async function getBackfillPlan(
  entityId: string,
  params: { months: number; limit: number }
): Promise<BackfillPlan> {
  const { data } = await apiClient.get<BackfillPlan>(
    `/admin/entities/${entityId}/backfill`,
    { params }
  );
  return data;
}

export async function applyBackfill(
  entityId: string,
  body: { months: number; limit: number; announcement_ids?: string[] }
): Promise<BackfillPlan> {
  const { data } = await apiClient.post<BackfillPlan>(
    `/admin/entities/${entityId}/backfill`,
    body
  );
  return data;
}

// ---------------------------------------------------------------------------
// Relationships tab (M5c surface, 2026-09-24) — ownership, director interests,
// who acts for the company, people. Accountable: every holding carries its
// filings, dates and resolution status.
// ---------------------------------------------------------------------------

export interface HoldingAssociate {
  name: string;
  association_type: string;
  role_label: string | null;
  entity_id: string | null;
  resolution_status: string | null;
}

export interface HoldingFiling {
  filing_id: string;
  filer_name_raw: string;
  form_type: string | null;
  transaction_type: string;
  previous_pct: number | null;
  current_pct: number | null;
  transaction_date: string | null;
  derivation_source: string | null;
  announcement_id: string | null;
  announcement_title: string | null;
  announcement_date: string | null;
  associates: HoldingAssociate[];
}

export interface HoldingPeriod {
  valid_from: string | null;
  valid_to: string | null;
  pct: number | null;
  form_type: string | null;
  start_unknown: boolean;
  current: boolean;
  filings: HoldingFiling[];
}

export interface HoldingGroup {
  entity_id: string | null;
  assayer_entity_id: string | null;
  name: string;
  entity_type: string;
  current: boolean;
  pct: number | null;
  since: string | null;
  ceased: string | null;
  history: HoldingPeriod[];
  associates: HoldingAssociate[];
}

export interface DirectorNotice {
  trade_id: string;
  notice_type: 'initial' | 'change' | 'final' | null;
  trade_type: string;
  on_market: boolean | null;
  trade_date: string | null;
  shares_changed: number | null;
  holding_after: number | null;
  trade_value_aud: number | null;
  derivation_source: string | null;
  announcement_id: string | null;
  announcement_title: string | null;
}

export interface DirectorInterest {
  person_entity_id: string | null;
  name: string;
  resolution_status: string | null;
  board_status: 'current' | 'former' | 'not_recorded';
  latest_holding: number | null;
  latest_date: string | null;
  left_but_holds: boolean;
  notices: DirectorNotice[];
}

export interface RelationshipEdge {
  relationship_type: string;
  kind: string | null;
  entity_id: string;
  assayer_entity_id?: string | null;
  name: string;
  entity_type: string;
  first_seen: string | null;
  last_seen: string | null;
  sightings: number | null;
  current: boolean;
  pct?: number | null;
  valid_from?: string | null;
  valid_to?: string | null;
  withdrawn?: boolean;
  via_firm?: string;
}

export interface UnresolvedHolderFiling {
  filing_id: string;
  filer_name_raw: string;
  filer_resolution_status: string | null;
  form_type: string | null;
  transaction_type: string;
  current_pct: number | null;
  on_date: string | null;
  announcement_id: string | null;
  announcement_title: string | null;
}

export interface EntityRelationships {
  entity_id: string;
  cynapse_entity_id: string | null;
  as_of: string | null;
  window_days: number;
  cynapse_error: string | null;
  holders: HoldingGroup[];
  holds: HoldingGroup[];
  director_interests: DirectorInterest[];
  acts_for: RelationshipEdge[];
  acts_for_via_firm: RelationshipEdge[];
  people: RelationshipEdge[];
  unresolved_holders: UnresolvedHolderFiling[];
}

export async function getEntityRelationships(entityId: string): Promise<EntityRelationships> {
  const { data } = await apiClient.get<EntityRelationships>(`/admin/entities/${entityId}/relationships`);
  return data;
}

export interface CynapseEntityRelationships {
  as_of: string;
  window_days: number;
  entities: { entity_id: string; name: string; entity_type: string }[];
  outgoing: RelationshipEdge[];
  incoming: RelationshipEdge[];
}

/** Any Cynapse entity (a holder that assayer does not track) — both directions. */
export async function getCynapseEntityRelationships(cynapseEntityId: string): Promise<CynapseEntityRelationships> {
  const { data } = await apiClient.get<CynapseEntityRelationships>(
    `/admin/relationships/entity/${cynapseEntityId}`,
  );
  return data;
}
